/**
 * Dashboard Design Tokens
 * Derived from Lawis Landing Page design system
 *
 * Design Philosophy:
 * - Luxury minimal aesthetic with gold accent
 * - DM Sans for body, Cormorant Garamond for headings
 * - Clean whites in light mode, pure blacks in dark mode
 * - Warm stone/zinc tones for borders and muted text
 */

// ─── Brand ────────────────────────────────────────────────────────────────────

export const BRAND = {
  gold: '#c9a96e',
  goldLight: '#d4b97e',
  goldDark: '#b8955a',
  goldSubtle: 'rgba(201, 169, 110, 0.12)',
  goldBorder: 'rgba(201, 169, 110, 0.3)',
};

// ─── Typography ───────────────────────────────────────────────────────────────

export const FONTS = {
  body: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  display: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
};

// ─── Light Mode ───────────────────────────────────────────────────────────────

export const LIGHT = {
  // Backgrounds
  bg: '#ffffff',
  bgSecondary: '#fafaf9',
  bgTertiary: '#f5f5f4',
  bgNavbar: '#fafaf9',

  // Surfaces (cards, modals)
  surface: '#ffffff',
  surfaceSecondary: '#fafaf9',
  surfaceHover: '#f5f5f4',

  // Text
  textPrimary: '#1c1917',
  textSecondary: '#78716c',
  textTertiary: '#a8a29e',
  textMuted: '#d6d3d1',

  // Borders
  borderPrimary: '#e7e5e4',
  borderSecondary: '#d6d3d1',
  borderSubtle: '#f5f5f4',

  // Shadows
  shadowSm: '0 2px 8px rgba(0, 0, 0, 0.06)',
  shadowMd: '0 4px 16px rgba(0, 0, 0, 0.08)',
  shadowLg: '0 8px 24px rgba(0, 0, 0, 0.12)',
  shadowCard: '0 2px 8px rgba(0, 0, 0, 0.04)',
  shadowElevated: '0 4px 16px rgba(0, 0, 0, 0.08)',
  shadowHover: '0 8px 24px rgba(0, 0, 0, 0.12)',
};

// ─── Dark Mode ────────────────────────────────────────────────────────────────

export const DARK = {
  // Backgrounds
  bg: '#000000',
  bgSecondary: '#0a0a0a',
  bgTertiary: '#111111',
  bgNavbar: '#0a0a0a',

  // Surfaces (cards, modals)
  surface: '#0a0a0a',
  surfaceSecondary: '#111111',
  surfaceHover: '#1a1a1a',

  // Text
  textPrimary: '#f5f5f5',
  textSecondary: '#a8a8a8',
  textTertiary: '#737373',
  textMuted: '#4a4a4a',

  // Borders
  borderPrimary: '#2d2d2d',
  borderSecondary: '#1a1a1a',
  borderSubtle: '#111111',

  // Shadows
  shadowSm: '0 2px 8px rgba(0, 0, 0, 0.5)',
  shadowMd: '0 4px 16px rgba(0, 0, 0, 0.6)',
  shadowLg: '0 8px 24px rgba(0, 0, 0, 0.7)',
  shadowCard: '0 2px 8px rgba(0, 0, 0, 0.5)',
  shadowElevated: '0 4px 24px rgba(0, 0, 0, 0.6)',
  shadowHover: '0 8px 40px rgba(0, 0, 0, 0.7)',
};

// ─── Accent / Status Colors ──────────────────────────────────────────────────

export const STATUS = {
  success: '#52c41a',
  successLight: '#6ee7b7',
  info: '#6366F1',
  infoLight: '#818cf8',
  warning: '#F59E0B',
  warningLight: '#fbbf24',
  error: '#EC4899',
  errorLight: '#f472b6',
};

// ─── Border Radius ────────────────────────────────────────────────────────────

export const RADIUS = {
  small: '6px',
  standard: '8px',
  large: '12px',
  xlarge: '16px',
  full: '9999px',
};

// ─── Spacing ──────────────────────────────────────────────────────────────────

export const SPACING = {
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  '3xl': '64px',
  '4xl': '80px',
};

// ─── Transitions ──────────────────────────────────────────────────────────────

export const TRANSITIONS = {
  fast: '0.1s cubic-bezier(0.4, 0, 0.2, 1)',
  mid: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

// ─── Helper ───────────────────────────────────────────────────────────────────

export const getTokens = (mode) => (mode === 'dark' ? DARK : LIGHT);

export default {
  BRAND,
  FONTS,
  LIGHT,
  DARK,
  STATUS,
  RADIUS,
  SPACING,
  TRANSITIONS,
  getTokens,
};
